// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore/lite';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Detect mobile/Safari where popups are unreliable
function isMobileBrowser() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

async function ensureUserDoc(user) {
  const userRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(userRef, {
      email: user.email,
      displayName: user.displayName || user.email,
      role: 'free',
      createdAt: serverTimestamp(),
    });
  }
}

const DEV_ROLES = ['free', 'team_member', 'team_leader', 'consultant', 'admin'];
const IS_DEV = process.env.NODE_ENV === 'development';

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser]   = useState(null);
  const [userProfile, setUserProfile]   = useState(null);
  const [devRole, setDevRole]           = useState(null); // dev-only override
  const [impersonatedUser, setImpersonatedUser] = useState(null); // admin "view as" target, { uid, ...userDoc } or null
  const [loading, setLoading]           = useState(true);

  // ── On mount: process any pending redirect result ────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function processRedirect() {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user && !cancelled) {
          await ensureUserDoc(result.user);
        }
      } catch (err) {
        // auth/missing-initial-state = storage partitioned or stale redirect
        // auth/no-auth-event = no redirect in progress — both are safe to ignore
        if (
          err.code !== 'auth/missing-initial-state' &&
          err.code !== 'auth/no-auth-event'
        ) {
          console.error('Redirect result error:', err);
        }
      }
    }

    processRedirect();
    return () => { cancelled = true; };
  }, []);

  // ── Auth state listener ──────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setUserProfile(snap.exists() ? snap.data() : null);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Auth methods ─────────────────────────────────────────────────────────

  async function register(email, password, displayName, inviteToken) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(cred.user, { url: `${window.location.origin}/verify-email-complete` });

    const profile = {
      email: cred.user.email,
      displayName,
      role: 'free',
      createdAt: serverTimestamp(),
    };

    // Auth must exist before we can read invitations (Firestore rules require isAuth()),
    // so the invite is resolved here and merged into the one and only profile write —
    // writing 'free' first and upgrading it in a second write raced with the
    // onAuthStateChanged listener's one-time profile read, sometimes leaving the
    // React session stuck on 'free' after a correct invite-role Firestore write.
    if (inviteToken) {
      const inviteRef = doc(db, 'invitations', inviteToken);
      const inviteSnap = await getDoc(inviteRef);
      if (inviteSnap.exists()) {
        const invite = inviteSnap.data();
        if (invite.email !== cred.user.email) {
          throw new Error('This invitation was sent to a different email address.');
        }
        profile.role = invite.role;
        profile.sourceInviteId = inviteToken;
        if (invite.teamId) profile.teamId = invite.teamId;
        await updateDoc(inviteRef, { status: 'accepted', acceptedAt: serverTimestamp(), uid: cred.user.uid });
      }
    }

    await setDoc(doc(db, 'users', cred.user.uid), profile);
    // onAuthStateChanged's getDoc can race ahead of the setDoc above and read the
    // doc before it exists, permanently caching a null userProfile (it only fires
    // once per auth transition). Set it directly here so registration is correct
    // regardless of how that race resolves.
    setUserProfile(profile);
    return cred;
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    // Always use redirect on mobile — popups are blocked by iOS Safari
    // and storage-partitioned environments
    if (isMobileBrowser()) {
      return signInWithRedirect(auth, provider);
    }

    // Desktop: try popup, fall back to redirect if blocked
    try {
      const cred = await signInWithPopup(auth, provider);
      await ensureUserDoc(cred.user);
      return cred;
    } catch (err) {
      if (
        err.code === 'auth/popup-blocked' ||
        err.code === 'auth/popup-closed-by-user' ||
        err.code === 'auth/cancelled-popup-request'
      ) {
        return signInWithRedirect(auth, provider);
      }
      throw err;
    }
  }

  async function logout() {
    return signOut(auth);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // In dev or for admin users, merge the overridden role into a copy of userProfile
  const isRealAdmin = userProfile?.role === 'admin';

  // Admin "view as user" — read-only, takes precedence over devRole
  function startImpersonation(targetUser) {
    if (!isRealAdmin) return;
    setDevRole(null);
    setImpersonatedUser(targetUser);
  }
  function stopImpersonation() {
    setImpersonatedUser(null);
  }

  const effectiveProfile = impersonatedUser
    ? impersonatedUser
    : (IS_DEV || isRealAdmin) && devRole && userProfile
      ? { ...userProfile, role: devRole }
      : userProfile;

  const effectiveUid = impersonatedUser ? impersonatedUser.uid : currentUser?.uid;

  const value = {
    currentUser,
    userProfile: effectiveProfile,
    effectiveUid,
    register,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    loading,
    // Dev tools (also available to admin users in production)
    devRole,
    setDevRole: (IS_DEV || isRealAdmin) ? setDevRole : undefined,
    realRole: userProfile?.role,
    DEV_ROLES: (IS_DEV || isRealAdmin) ? DEV_ROLES : undefined,
    // Admin "view as user"
    impersonatedUser,
    isImpersonating: !!impersonatedUser,
    startImpersonation: isRealAdmin ? startImpersonation : undefined,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
